import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { environment } from '@environments/environment';
import { ClientSession } from 'src/core/interfaces/client-session.interfaces';
import { LegalAcceptanceService } from 'src/core/services/legal-acceptance/legal-acceptance.service';

const STORAGE_KEY = 'pc_client_session';

interface ApiSession {
  clienteUserId: string;
  nombre: string;
  companyId: string;
  sesionExpiraAt: string | null;
  inactividadSegundos: number | null;
}

@Injectable({ providedIn: 'root' })
export class ClientSessionService {
  private readonly http = inject(HttpClient);
  private readonly legalAcceptance = inject(LegalAcceptanceService);
  private readonly baseUrl = `${environment.workflowApiUrl}/portal/client`;

  private readonly sessionSignal = signal<ClientSession | null>(this.readFromStorage());

  readonly session = this.sessionSignal.asReadonly();

  get current(): ClientSession | null {
    return this.sessionSignal();
  }

  setSession(session: ClientSession): void {
    this.sessionSignal.set(session);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }

  // Cerrar sesión también cierra la compuerta legal: el próximo login vuelve a
  // verificar la versión vigente, que es donde la HU ubica la re-aceptación.
  clearSession(): void {
    this.sessionSignal.set(null);
    sessionStorage.removeItem(STORAGE_KEY);
    this.legalAcceptance.clearGate();
  }

  restoreFromCookie(): Observable<ClientSession | null> {
    return this.http.get<{ data: ApiSession }>(`${this.baseUrl}/session`).pipe(
      map((response) => {
        const session = response.data;
        if (!session?.clienteUserId) return null;
        return {
          clientUserId: session.clienteUserId,
          companyId: session.companyId,
          fullName: session.nombre,
          idleTimeoutSeconds: session.inactividadSegundos ?? null,
          sessionExpiresAt: session.sesionExpiraAt ?? null,
        };
      }),
      tap((session) => {
        if (session) this.setSession(session);
      }),
      catchError(() => of(null))
    );
  }

  logout(): Observable<{ cerrada: boolean }> {
    return this.http
      .post<{ cerrada: boolean }>(`${this.baseUrl}/logout`, {})
      .pipe(tap(() => this.clearSession()));
  }

  // Una sesión restaurada de otro tenant que el de la URL se cierra y vuelve al OTP.
  enforceTenant(session: ClientSession | null, tenantCompanyId: string | null): Observable<ClientSession | null> {
    if (!session || !tenantCompanyId || session.companyId === tenantCompanyId) return of(session);

    return this.logout().pipe(
      catchError(() => {
        this.clearSession();
        return of(null);
      }),
      map(() => null)
    );
  }

  private readFromStorage(): ClientSession | null {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ClientSession) : null;
  }
}
